import type { ReactNode } from 'react';
import type { ToastOptions } from 'react-toastify';

import { theme } from '@adflow/frontend/ui-theme';

import { createToast } from './create-toast';
import { joinNodes } from './join-nodes';

export function createCancelToast(message: ReactNode) {
	return createToast(({ t }) => <>{t('toast.canceled')}</>, message, 'cancel', {
		style: {
			color: theme.colors.text.primary,
			backgroundColor: theme.colors.toasts.cancel,
		},
	});
}

interface ErrorToastOptions extends Pick<
	ToastOptions,
	'autoClose' | 'onClick' | 'style' | 'toastId' | 'closeOnClick'
> {
	errorCodes?: string[];
}
export function createErrorToast(
	message: ReactNode,
	{
		errorCodes,
		autoClose = 1000 * 20, // 20 seconds
		style,
		...otherOptions
	}: ErrorToastOptions = {}
) {
	return createToast(
		({ t }) => (
			<>
				{t('toast.error')}
				{errorCodes && errorCodes.length > 0 ? (
					<>
						:{' '}
						{joinNodes(
							errorCodes.map((errorCode, index) => (
								<code key={index}>{errorCode}</code>
							)),
							', '
						)}
					</>
				) : null}
			</>
		),
		message,
		'error',
		{
			...otherOptions,
			autoClose,
			style: {
				color: theme.colors.palettes.primary.contrastText,
				backgroundColor: theme.colors.toasts.error,
				...style,
			},
		}
	);
}

export function createAuthErrorToast(title: ReactNode, message: ReactNode) {
	return createToast(() => title, message, 'error', {
		autoClose: 1000 * 20, // 20 seconds
		style: {
			color: theme.colors.palettes.primary.contrastText,
			backgroundColor: theme.colors.toasts.error,
		},
	});
}

export function createSuccessToast(message: ReactNode, seconds = 3) {
	return createToast(({ t }) => <>{t('toast.success')}</>, message, 'success', {
		style: {
			color: theme.colors.palettes.primary.contrastText,
			backgroundColor: theme.colors.toasts.success,
		},
		autoClose: seconds * 1000,
	});
}

export function createArchivedToast(message: ReactNode) {
	return createToast(({ t }) => <>{t('toast.success')}</>, message, 'success', {
		style: {
			color: theme.colors.text.primary,
			backgroundColor: theme.colors.toasts.archive,
		},
	});
}

export function createWarningToast(message: ReactNode) {
	return createToast(({ t }) => <>{t('toast.warning')}</>, message, 'warning', {
		style: {
			color: theme.colors.text.primary,
			backgroundColor: theme.colors.toasts.warning,
		},
	});
}

export function createDraftToast() {
	return createToast(({ t }) => <>{t('toast.draft')}</>, null, 'warning', {
		style: {
			color: theme.colors.text.primary,
			backgroundColor: theme.colors.toasts.draft,
		},
	});
}
